<template>
  <v-banner
    :color="greenColor"
    :value="showDownloadBanner"
    sticky
    transition="slide-y-transition"
    class="download-case-banner"
    :class="{ 'download-case-banner--streaming': streamDownloadInProgress }"
    data-testid="case-detail-case-downloading"
  >
    <div class="download-case-banner__content">
      <v-progress-circular
        v-if="streamDownloadInProgress"
        indeterminate
        size="18"
        width="2"
        color="white"
        class="download-case-banner__spinner"
      />
      <span>
        {{ $t('global.processingDownload')
        }}<i class="ellipsis"><i>.</i><i>.</i><i>.</i></i>
      </span>
    </div>
    <v-progress-linear
      v-if="streamDownloadInProgress"
      indeterminate
      color="white"
      background-opacity="0.25"
      class="download-case-banner__progress"
    />
    <template v-slot:actions>
      <v-btn text color="white" @click="cancelDownload">
        {{ $t('global.cancel') }}
      </v-btn>
    </template>
  </v-banner>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import * as Colors from '@/constants/colors'
import { mapActions, mapState } from 'vuex'
import {
  startStreamDownloadInWorker,
  supportsStreamDownloadWorker,
} from '@/utils/download/streamDownloadWorkerClient'

type DownloadItem = {
  url: string
  fileName: string
  fileType: 'mp4' | 'png' | 'zip' | 'other'
  isLarge: boolean
}

type DownloadRunStats = {
  successCount: number
  failedItems: string[]
}

export default defineComponent({
  name: 'DownloadCaseBanner',
  data() {
    return {
      localCancel: false,

      // prevent duplicate concurrent download sessions
      downloadInProgress: false,
      streamDownloadInProgress: false,
      downloadSessionId: 0,

      // tuning
      smallFileConcurrency: 2,
      maxSmallFileConcurrency: 3,
      smallFileIntervalMs: 260,
      largeFileIntervalMs: 1000,
      largeBlobCoolDownMs: 220,
      batchYieldIntervalMs: 220,
      zipStartDelayMs: 450,
      downloadRequestTimeoutMs: 120000,
      largeDownloadTimeoutMs: 1200000,
      downloadRetryCount: 1,
      downloadRetryBackoffMs: 900,
      maxBatchDurationMs: 15 * 60 * 1000,
      blobUrlRevokeDelayMs: 60000,

      activeDownloadControllers: new Set<AbortController>() as Set<AbortController>,
      activeWorkerCancels: new Set<() => void>() as Set<() => void>,

      pendingDownloadActionLink: '',
      pendingZipUrl: '',
      pendingArtifacts: [] as DownloadItem[],
    }
  },
  computed: {
    ...mapState('WebSocket', [
      'downloadReady',
      'downloadPayload',
      'downloadError',
    ]),
    ...mapState('Case', [
      'caseDownloading',
      'caseDownloadingID',
      'currentCase',
      'currentCaseGroup',
    ]),
    greenColor() {
      return Colors.CSS_HEX_GREEN
    },
    showDownloadBanner() {
      return this.caseDownloading || this.streamDownloadInProgress
    },
  },
  mounted() {
    window.addEventListener(
      'toast-download-link-click',
      this.onToastDownloadLinkClick
    )
  },
  beforeUnmount() {
    window.removeEventListener(
      'toast-download-link-click',
      this.onToastDownloadLinkClick
    )
  },
  methods: {
    ...mapActions('Case', ['clickCancelDownload']),
    ...mapActions('ToastMessage', [
      'callFailureToast',
      'callSuccessToast',
      'callInfoToast',
    ]),
    ...mapActions('WebSocket', ['resetDownload']),

    cancelDownload() {
      this.localCancel = true
      this.downloadInProgress = false
      this.streamDownloadInProgress = false
      this.abortActiveDownloadRequests()
      this.clickCancelDownload()
      this.resetDownload()
    },

    finalizeDownloadState() {
      setTimeout(() => {
        if (this.streamDownloadInProgress) {
          this.finalizeDownloadState()
          return
        }

        this.abortActiveDownloadRequests()
        this.cancelDownload()
        this.localCancel = false
      }, 750)
    },

    sleep(ms: number) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },

    getDownloadTimeoutMs(item: DownloadItem) {
      if (item.isLarge) return this.largeDownloadTimeoutMs
      return this.downloadRequestTimeoutMs
    },

    withTimeout<T>(
      promise: Promise<T>,
      timeoutMs: number,
      label = 'operation'
    ) {
      let timer: number | null = null
      const timeoutPromise = new Promise<T>((_, reject) => {
        timer = window.setTimeout(() => {
          reject(new Error(`${label} timeout`))
        }, timeoutMs)
      })

      return Promise.race([promise, timeoutPromise]).finally(() => {
        if (timer !== null) window.clearTimeout(timer)
      }) as Promise<T>
    },

    createEmptyDownloadStats(): DownloadRunStats {
      return {
        successCount: 0,
        failedItems: [],
      }
    },

    mergeDownloadStats(
      first: DownloadRunStats,
      second: DownloadRunStats
    ): DownloadRunStats {
      return {
        successCount: first.successCount + second.successCount,
        failedItems: [...first.failedItems, ...second.failedItems],
      }
    },

    abortActiveDownloadRequests() {
      if (!(this.activeDownloadControllers instanceof Set)) return

      this.activeDownloadControllers.forEach((controller) => {
        try {
          controller.abort()
        } catch {
          // ignore abort errors
        }
      })

      this.activeDownloadControllers.clear()

      if (this.activeWorkerCancels instanceof Set) {
        this.activeWorkerCancels.forEach((cancelJob) => {
          try {
            cancelJob()
          } catch {
            // ignore cancel errors
          }
        })
        this.activeWorkerCancels.clear()
      }
    },

    isSafari() {
      const ua = navigator.userAgent.toLowerCase()
      return (
        ua.includes('safari') && !ua.includes('chrome') && !ua.includes('edg')
      )
    },

    isFirefox() {
      return navigator.userAgent.toLowerCase().includes('firefox')
    },

    getRuntimeConcurrency() {
      // Firefox and Safari are more likely to block multiple downloads, so fall back to sequential mode
      if (this.isFirefox() || this.isSafari()) return 1

      const cpu = Number(navigator.hardwareConcurrency) || 0
      if (cpu >= 8) return this.maxSmallFileConcurrency
      return this.smallFileConcurrency
    },

    getAdaptiveIntervals(totalFiles: number) {
      // When there are many files, increase the spacing to reduce short-term browser load spikes.
      if (totalFiles >= 50) {
        return {
          smallIntervalMs: 400,
          largeIntervalMs: 1300,
          batchYieldMs: 320,
        }
      }

      if (totalFiles >= 20) {
        return {
          smallIntervalMs: 320,
          largeIntervalMs: 1100,
          batchYieldMs: 260,
        }
      }

      return {
        smallIntervalMs: this.smallFileIntervalMs,
        largeIntervalMs: this.largeFileIntervalMs,
        batchYieldMs: this.batchYieldIntervalMs,
      }
    },

    createToastDownloadActionLink() {
      return `action:case-download:${Date.now()}:${Math.random()
        .toString(16)
        .slice(2)}`
    },

    queueDownloadViaToast(artifactItems: DownloadItem[], zipUrl = '') {
      const validZipUrl = this.isDownloadableUrl(zipUrl)
        ? this.sanitizeDownloadUrl(zipUrl)
        : ''

      const validArtifacts = (Array.isArray(artifactItems)
        ? artifactItems
        : []
      ).filter((x) => x && this.isDownloadableUrl(x.url))

      if (!validZipUrl && validArtifacts.length === 0) {
        this.callFailureToast({
          toastMessage: this.$t('toastMessage.caseDownloadFailed'),
        })
        return
      }

      this.pendingZipUrl = validZipUrl
      this.pendingArtifacts = [...validArtifacts]
      this.pendingDownloadActionLink = this.createToastDownloadActionLink()

      this.callSuccessToast({
        toastMessage: this.$t('toastMessage.clickDownloadLink'),
        toastDownloadLink: this.pendingDownloadActionLink,
      })
    },

    async onToastDownloadLinkClick(event: Event) {
      const actionLink = String((event as CustomEvent<string>)?.detail || '')
      if (!actionLink) return
      if (actionLink !== this.pendingDownloadActionLink) return
      if (this.streamDownloadInProgress) return

      const pendingArtifacts = [...this.pendingArtifacts]
      const pendingZipUrl = this.pendingZipUrl

      this.pendingDownloadActionLink = ''
      this.pendingArtifacts = []
      this.pendingZipUrl = ''
      this.streamDownloadInProgress = true

      try {
        await this.downloadArtifactsByLocation(pendingArtifacts, pendingZipUrl)
      } finally {
        this.streamDownloadInProgress = false
      }
    },

    parseDownloadPayload(payload: any) {
      if (Array.isArray(payload)) {
        return {
          zipUrl: payload[0] || '',
          isUnder2G:
            typeof payload[1] === 'boolean'
              ? payload[1]
              : String(payload[1]).toLowerCase() !== 'false',
        }
      }

      if (payload && typeof payload === 'object') {
        return {
          zipUrl: payload.zipUrl || payload.zip || '',
          isUnder2G:
            typeof payload.isUnder2G === 'boolean' ? payload.isUnder2G : true,
        }
      }

      return {
        zipUrl: payload || '',
        isUnder2G: true,
      }
    },

    sanitizeDownloadUrl(url: unknown) {
      if (typeof url !== 'string') return ''
      return url
        .trim()
        .replace(/^['"\[]+/, '')
        .replace(/['"\],]+$/, '')
    },

    isDownloadableUrl(url: unknown) {
      const normalizedUrl = this.sanitizeDownloadUrl(url)
      if (!normalizedUrl) return false

      const invalidKeywords = ['sd card', 'nvr', 'hls', 'undefined', 'null']
      const lower = normalizedUrl.toLowerCase()

      if (invalidKeywords.includes(lower)) return false
      if (lower.startsWith('javascript:')) return false

      return (
        normalizedUrl.startsWith('http://') ||
        normalizedUrl.startsWith('https://') ||
        normalizedUrl.startsWith('/')
      )
    },

    isCrossOriginDownloadUrl(url: unknown) {
      const normalizedUrl = this.sanitizeDownloadUrl(url)
      if (!this.isDownloadableUrl(normalizedUrl)) return false

      try {
        const resolvedUrl = new URL(normalizedUrl, window.location.href)
        return resolvedUrl.origin !== window.location.origin
      } catch {
        // If URL parsing fails, avoid direct-link download fallback.
        return true
      }
    },

    inferFileTypeFromUrl(url: string): DownloadItem['fileType'] {
      const lower = url.toLowerCase()
      if (lower.includes('.mp4')) return 'mp4'
      if (lower.includes('.png')) return 'png'
      if (lower.includes('.zip')) return 'zip'
      return 'other'
    },

    inferFileNameFromUrl(url: string, fallback = 'download') {
      try {
        const noQuery = url.split('?')[0]
        const name = noQuery.substring(noQuery.lastIndexOf('/') + 1)
        return name || fallback
      } catch {
        return fallback
      }
    },

    getArtifactLocations() {
      const items: DownloadItem[] = []

      const pushArtifact = (artifact: any) => {
        const rawUrl = artifact?.artifactPath || ''
        const url = this.sanitizeDownloadUrl(rawUrl)
        if (!this.isDownloadableUrl(url)) return

        const fileType = this.inferFileTypeFromUrl(url)
        // Only handle MP4 and PNG files
        if (fileType !== 'mp4' && fileType !== 'png') return

        items.push({
          url,
          fileType,
          fileName:
            artifact?.artifactName ||
            this.inferFileNameFromUrl(
              url,
              `${this.caseDownloadingID || 'case'}`
            ),
          isLarge: fileType === 'mp4',
        })
      }

      const routeName = String(this.$route?.name || '').toLowerCase()
      const routePath = String(this.$route?.path || '').toLowerCase()

      const isMultiCaseRoute =
        routeName.includes('multicase') ||
        routePath.includes('/case-group/') ||
        routePath.includes('/shared-multi-cases/')

      const isCurrentCaseDownload =
        this.caseDownloadingID &&
        this.currentCase?.caseNumber &&
        String(this.caseDownloadingID) === String(this.currentCase.caseNumber)

      const isCurrentCaseGroupDownload =
        this.caseDownloadingID &&
        this.currentCaseGroup?.caseGroupName &&
        String(this.caseDownloadingID) ===
          String(
            `${this.currentCaseGroup.caseGroupName} - ${this.currentCaseGroup.caseGroupId}`
          )

      const useCaseGroupArtifacts =
        isCurrentCaseGroupDownload ||
        (!isCurrentCaseDownload && isMultiCaseRoute)

      if (useCaseGroupArtifacts) {
        if (this.currentCaseGroup?.cases?.length) {
          this.currentCaseGroup.cases.forEach((caseItem: any) => {
            if (!caseItem?.caseArtifacts?.length) return
            caseItem.caseArtifacts.forEach((artifact: any) =>
              pushArtifact(artifact)
            )
          })
        }
      } else if (this.currentCase?.caseArtifacts?.length) {
        this.currentCase.caseArtifacts.forEach((artifact: any) =>
          pushArtifact(artifact)
        )
      }

      // Deduplicate by URL
      const uniq = new Map<string, DownloadItem>()
      items.forEach((item) => {
        if (!uniq.has(item.url)) uniq.set(item.url, item)
      })

      return [...uniq.values()]
    },

    triggerDownload(url: string, fileName = '') {
      const cleanUrl = this.sanitizeDownloadUrl(url)
      if (!this.isDownloadableUrl(cleanUrl) || this.localCancel) return false

      const link = document.createElement('a')
      link.href = cleanUrl
      link.rel = 'noopener'
      // directly download without opening a new tab
      if (fileName) link.setAttribute('download', fileName)
      else link.setAttribute('download', '')

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      return true
    },

    triggerBlobDownload(blob: Blob, fileName = '') {
      if (this.localCancel) return false

      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.rel = 'noopener'
      link.setAttribute('download', fileName || 'download')

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl)
      }, this.blobUrlRevokeDelayMs)

      return true
    },

    safeTriggerDownload(url: string, fileName = '') {
      try {
        return this.triggerDownload(url, fileName)
      } catch {
        return false
      }
    },

    async fetchBlobWithTimeout(url: string, timeoutMs: number) {
      const controller = new AbortController()
      const signal = controller.signal
      this.activeDownloadControllers.add(controller)

      let timer: number | null = null

      try {
        timer = window.setTimeout(() => {
          controller.abort()
        }, timeoutMs)

        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
          signal,
        })

        if (!response.ok) {
          throw new Error(`download status ${response.status}`)
        }

        return await response.blob()
      } finally {
        if (timer !== null) window.clearTimeout(timer)
        this.activeDownloadControllers.delete(controller)
      }
    },

    async fetchBlobWithWorker(url: string, timeoutMs: number) {
      const downloadJob = startStreamDownloadInWorker({
        url,
        timeoutMs,
        credentials: 'include',
      })

      this.activeWorkerCancels.add(downloadJob.cancel)

      try {
        return await downloadJob.promise
      } finally {
        this.activeWorkerCancels.delete(downloadJob.cancel)
      }
    },

    async downloadItemWithFallback(item: DownloadItem) {
      if (this.localCancel) return false

      const requiresOriginCheck =
        item.fileType === 'mp4' || item.fileType === 'png'
      const isCrossOrigin =
        requiresOriginCheck && this.isCrossOriginDownloadUrl(item.url)

      // Only mp4/png enforce same-origin check; zip keeps direct anchor download.
      const shouldUseBlobFetch = requiresOriginCheck
        ? isCrossOrigin
        : !item.isLarge && item.fileType !== 'zip'

      if (!shouldUseBlobFetch) {
        return this.safeTriggerDownload(item.url, item.fileName)
      }

      const maxAttempts = Math.max(1, this.downloadRetryCount + 1)
      const timeoutMs = this.getDownloadTimeoutMs(item)

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (this.localCancel) return false

        try {
          // Keep a small gap before each large blob fetch to reduce CPU/network bursts.
          if (item.isLarge) {
            await this.sleep(this.largeBlobCoolDownMs)
          }

          let blob: Blob

          if (supportsStreamDownloadWorker()) {
            try {
              blob = await this.fetchBlobWithWorker(item.url, timeoutMs)
            } catch {
              // Worker failures fall back to main-thread fetch for compatibility.
              blob = await this.fetchBlobWithTimeout(item.url, timeoutMs)
            }
          } else {
            blob = await this.fetchBlobWithTimeout(item.url, timeoutMs)
          }

          return this.triggerBlobDownload(blob, item.fileName)
        } catch (error) {
          console.error(
            `Download attempt ${attempt} failed for ${item.url}. Retrying...`,
            error
          )
          if (attempt < maxAttempts) {
            await this.sleep(this.downloadRetryBackoffMs * attempt)
            continue
          }
        }
      }

      // For cross-origin mp4/png never fallback to direct-link; it can navigate away.
      if (requiresOriginCheck && isCrossOrigin) return false

      // Fallback path keeps compatibility for same-origin failures.
      return this.safeTriggerDownload(item.url, item.fileName)
    },

    async downloadSequential(
      items: DownloadItem[],
      intervalMs: number
    ): Promise<DownloadRunStats> {
      const stats = this.createEmptyDownloadStats()

      for (let i = 0; i < items.length; i++) {
        if (this.localCancel) return stats
        const item = items[i]

        try {
          const success = await this.withTimeout(
            this.downloadItemWithFallback(item),
            this.getDownloadTimeoutMs(item) + 10000,
            'sequential download'
          )
          if (success) stats.successCount += 1
          else stats.failedItems.push(item.fileName || item.url)
        } catch {
          stats.failedItems.push(item.fileName || item.url)
        }

        // Yield occasionally so the UI thread can breathe during large queues.
        if (i > 0 && i % 5 === 0) {
          await this.sleep(60)
        }

        await this.sleep(intervalMs)
      }

      return stats
    },

    async downloadConcurrent(
      items: DownloadItem[],
      concurrency: number,
      intervalMs: number,
      batchYieldMs: number
    ): Promise<DownloadRunStats> {
      const stats = this.createEmptyDownloadStats()
      if (!items.length) return stats

      const size = Math.max(1, Math.min(concurrency, items.length))

      for (let start = 0; start < items.length; start += size) {
        if (this.localCancel) return stats

        const currentBatch = items.slice(start, start + size)
        const batchResults = await Promise.all(
          currentBatch.map(async (item, index) => {
            if (this.localCancel) return
            if (index > 0) await this.sleep(intervalMs * index)

            try {
              const success = await this.withTimeout(
                this.downloadItemWithFallback(item),
                this.getDownloadTimeoutMs(item) + 10000,
                'concurrent download'
              )
              return {
                success,
                fileName: item.fileName || item.url,
              }
            } catch {
              return {
                success: false,
                fileName: item.fileName || item.url,
              }
            }
          })
        )

        batchResults.forEach((result) => {
          if (!result) return
          if (result.success) stats.successCount += 1
          else stats.failedItems.push(result.fileName)
        })

        if (this.localCancel) return stats

        await this.sleep(batchYieldMs)
      }

      return stats
    },

    async downloadArtifactsByLocation(
      artifactItems: DownloadItem[],
      fallbackZipUrl = ''
    ) {
      try {
        const validZipUrl = this.isDownloadableUrl(fallbackZipUrl)
          ? this.sanitizeDownloadUrl(fallbackZipUrl)
          : ''

        const validArtifacts = (Array.isArray(artifactItems)
          ? artifactItems
          : []
        ).filter((x) => x && this.isDownloadableUrl(x.url))

        const allArtifacts = validArtifacts
        const adaptive = this.getAdaptiveIntervals(allArtifacts.length)

        if (!validZipUrl && allArtifacts.length === 0) {
          this.callFailureToast({
            toastMessage: this.$t('toastMessage.caseDownloadFailed'),
          })
          return
        }

        if (this.localCancel) return

        const runStats = await this.withTimeout(
          (async () => {
            // trigger zip first
            if (validZipUrl) {
              await this.downloadItemWithFallback({
                url: validZipUrl,
                fileName: `CaseID_${this.caseDownloadingID || 'download'}.zip`,
                fileType: 'zip',
                isLarge: true,
              })
              await this.sleep(this.zipStartDelayMs)
            }

            const serialFiles = allArtifacts.filter((x) => x.fileType === 'mp4')
            const concurrentFiles = allArtifacts.filter(
              (x) => x.fileType !== 'mp4'
            )

            // mp4 downloads stay sequential to limit memory/network pressure.
            const serialStats = await this.downloadSequential(
              serialFiles,
              adaptive.largeIntervalMs
            )

            if (this.localCancel) return serialStats

            // non-mp4 files can use low concurrency.
            const concurrentStats = await this.downloadConcurrent(
              concurrentFiles,
              this.getRuntimeConcurrency(),
              adaptive.smallIntervalMs,
              adaptive.batchYieldMs
            )

            return this.mergeDownloadStats(serialStats, concurrentStats)
          })(),
          this.maxBatchDurationMs,
          'batch download'
        )

        if (this.localCancel) return

        if (runStats.failedItems.length > 0) {
          this.callInfoToast({
            toastMessageLineTwo: `${runStats.successCount}/${allArtifacts.length} files started. ${runStats.failedItems.length} failed.`,
            toastMessage: this.$t('toastMessage.caseDownloadFailed'),
          })
        }
      } catch {
        this.callFailureToast({
          toastMessage: this.$t('toastMessage.caseDownloadFailed'),
        })
      }
    },
  },

  watch: {
    caseDownloading() {
      if (this.caseDownloading) this.localCancel = false
    },

    async downloadReady() {
      if (!this.downloadReady) return
      if (this.downloadInProgress) return

      this.downloadInProgress = true
      const sessionId = ++this.downloadSessionId

      try {
        const { zipUrl, isUnder2G } = this.parseDownloadPayload(
          this.downloadPayload
        )

        // under 2G: zip only; over 2G: zip + case artifacts (mp4/png)
        const artifactItems = isUnder2G ? [] : this.getArtifactLocations()
        this.queueDownloadViaToast(artifactItems, zipUrl)
      } catch {
        this.callFailureToast({
          toastMessage: this.$t('toastMessage.caseDownloadFailed'),
        })
      } finally {
        // Finalize only the current active session
        if (sessionId === this.downloadSessionId) {
          this.finalizeDownloadState()
          this.downloadInProgress = false
        }
      }
    },

    downloadError() {
      if (this.downloadError) {
        this.callFailureToast({
          toastMessage: this.$t('toastMessage.caseDownloadFailed'),
        })
        setTimeout(() => {
          this.cancelDownload()
        }, 1000)
        this.localCancel = false
        this.downloadInProgress = false
      }
    },
  },
})
</script>

<style lang="scss">
.download-case-banner {
  font-weight: 800;

  &__content {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 24px;
  }

  &__spinner {
    flex: 0 0 auto;
  }

  &__progress {
    margin-top: 8px;
  }

  &--streaming {
    .v-banner__text {
      width: 100%;
    }
  }

  .v-banner__wrapper {
    padding-right: 3rem !important;
    padding-left: 5rem !important;
  }
  .ellipsis i {
    opacity: 0;
    -webkit-animation: dot 1.3s infinite;
    -webkit-animation-delay: 0s;
  }

  .ellipsis i + i {
    opacity: 0;
    -webkit-animation: dot 1.3s infinite;
    -webkit-animation-delay: 0.2s;
  }

  .ellipsis i + i + i {
    opacity: 0;
    -webkit-animation: dot 1.3s infinite;
    -webkit-animation-delay: 0.3s;
  }

  @-webkit-keyframes dot {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
}
</style>
